import React, { useState, useCallback, useEffect } from 'react';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/core/styles';
import {
  Form,
  Button,
  IconButton,
  Table,
  Dialog,
  Typography,
} from '../../../../components';
import { Container } from '../../../../layouts';

const useStyles = makeStyles((theme) => ({
  addDesignation: {
    marginTop: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  saveBtn: {
    marginRight: theme.spacing(1),
  },
}));

const Designations = (props) => {
  const { disabled, formik, rows, fields, columns } = props;
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);
  const [designation, setDesignation] = useState({});
  const [editingKey, setEditingKey] = useState(null);

  const [prevName, setPrevName] = useState(formik.values.name);
  const [timer, setTimer] = useState();

  const getKey = useCallback(
    (designation) => {
      return props.getKey({ ...designation, name: formik.values.name });
    },
    [formik.values.name, props]
  );

  useEffect(() => {
    // stop executing if name hasnt changed
    if (prevName === formik.values.name) return;

    setPrevName(formik.values.name);

    const debounce = 1000;
    clearTimeout(timer);
    setTimer(
      setTimeout(() => {
        const designations = {};
        Object.keys(formik.values.designations).forEach((key) => {
          const item = { ...formik.values.designations[key] };
          const newKey = getKey(item);
          designations[newKey] = item;
        });
        formik.setFieldValue('designations', designations);
      }, debounce)
    );
  }, [formik, prevName, setPrevName, timer, setTimer, getKey]);

  const handleAddDesignation = useCallback(() => {
    setEditingKey(null);
    setDesignation({});
    setOpen(true);
  }, [setOpen, setDesignation, setEditingKey]);

  const createEditHandler = useCallback(
    (key) => () => {
      setEditingKey(key);
      setDesignation({ ...formik.values.designations[key] });
      setOpen(true);
    },
    [setOpen, setDesignation, setEditingKey, formik.values.designations]
  );

  const createDeleteHandler = useCallback(
    (key) => () => {
      const designations = { ...formik.values.designations };
      delete designations[key];
      formik.setFieldValue(`designations`, designations);
    },
    [formik]
  );

  const handleCancelButton = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleSaveButton = useCallback(() => {
    setOpen(false);
    const key = getKey(designation);
    if (editingKey) {
      const designations = { ...formik.values.designations };
      delete designations[editingKey];
      designations[key] = { ...designation };
      formik.setFieldValue(`designations`, designations);
    } else {
      formik.setFieldValue(`designations.${key}`, { ...designation });
    }
  }, [setOpen, formik, getKey, designation, editingKey]);

  return (
    <Container className={props.className} disableGutters>
      <Typography variant="h6">Designations</Typography>
      <Form.Groupbox>
        <Table
          size="small"
          columns={columns}
          rows={rows.map((row) => {
            return {
              ...row,
              ...(!disabled ? {
                edit: (
                  <IconButton
                    aria-label="edit"
                    onClick={createEditHandler(getKey(row))}
                  >
                    <EditIcon/>
                  </IconButton>
                ),
                delete: (
                  <IconButton
                    aria-label="delete"
                    onClick={createDeleteHandler(getKey(row))}
                  >
                    <DeleteIcon/>
                  </IconButton>
                ),
              } : {})
            };
          })}
          keyField="designation"
        />
        {!disabled && (
          <Button
            variant="outlined"
            color="primary"
            margin="normal"
            onClick={handleAddDesignation}
            className={classes.addDesignation}
          >
            Add Designation
          </Button>
        )}
      </Form.Groupbox>
      <Dialog open={open}>
        <Container maxWidth="xs">
          <Typography>Add Designation</Typography>
          {open
            ? fields.map((field) => {
                return (
                  <Form.TextField
                    key={field.name}
                    onChange={(e) => {
                      const newDesignation = { ...designation };
                      newDesignation[field.name] = e.target.value;
                      setDesignation(newDesignation);
                    }}
                    value={designation[field.name]}
                    margin="dense"
                    label={field.label}
                    size="small"
                    fullWidth
                  />
                );
              })
            : null}
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveButton}
              className={classes.saveBtn}
            >
              Save
            </Button>
            <Button variant="contained" onClick={handleCancelButton}>
              Cancel
            </Button>
          </div>
        </Container>
      </Dialog>
    </Container>
  );
};

export default Designations;
